import { useContext, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { UserContext } from "../Contexts/UserContext";
import {
  Table,
  TableContainer,
  Paper,
  TableCell,
  TableRow,
  TableHead,
  TableBody,
  Box,
  TableFooter,
  TablePagination,
  TextField,
} from "@material-ui/core";
import { checkPermission } from "../utils/Roles";
import { useStyles } from "../Styles/styles";
import { withStyles } from "@material-ui/core/styles";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import CheckboxWithLabel from "./CheckboxWithLabel";
import { GET_RADIO_LOG } from "../Queries/GetRadioLog";
import { GET_ORIS_QUERY } from "../Queries/GetOrisQuery";
import {
  formatOfficerName,
  checkLocalStorageAge,
  localStoreData,
  sortByOriName,
  localGetData,
} from "../utils/utils";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { ThemeProvider } from "@material-ui/core/styles";
import { useCookies } from "react-cookie";
import HelpIconButton from "./HelpIconButton";
import { SetupContext } from "../Contexts/SetupContext";
import { darkTheme, lightTheme } from "../Styles/styles";
import moment from "moment";

const StyledTableHeader = withStyles(() => ({
  root: {
    background: "#7a8697",
    height: "fit-content",
    padding: 0,
    textAlign: "center",
    color: "white",
    fontSize: "1rem",
    borderRight: "2px solid #e3e2e2",
  },
}))(TableCell);

const LinkButton = ({ handleIncidentOpening, incident, user }) => {
  const { locked, sealed } = incident || {};
  const canViewLocked = checkPermission(
    user?.userRoles,
    "view_locked_incident",
    "ALL",
    "",
    incident?.id,
  );
  const canViewSealed = checkPermission(
    user?.userRoles,
    "view_sealed_incident",
    "ALL",
    "",
    incident?.id,
  );
  const disabled =
    (!canViewLocked && locked) || (!canViewSealed && sealed) ? true : false;
  return (
    <button
      style={
        disabled
          ? {
              backgroundColor: "transparent",
              color: "#7d7d7d",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "none",
              display: "inline",
              margin: "0",
              padding: "0",
              "&:hover": {
                textDecoration: "none",
              },
              "&:focus": {
                textDecoration: "none",
              },
            }
          : {
              backgroundColor: "transparent",
              color: "#6096d1",
              fontWeight: "bold",
              fontSize: "1rem",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
              display: "inline",
              margin: "0",
              padding: "0",
              "&:hover": {
                textDecoration: "none",
              },
              "&:focus": {
                textDecoration: "none",
              },
            }
      }
      data-cy={"tablecell-incident-number-link-" + incident?.id}
      onClick={
        disabled
          ? () => alert("You do not have permission to view this incident")
          : () => {
              handleIncidentOpening(incident?.id, true);
            }
      }
    >
      {incident?.incidentNumber}
    </button>
  );
};

const LogsTabWrapper = ({ handleIncidentOpening }) => {
  return (
    <div style={{ height: "100%" }}>
      <LogsTab handleIncidentOpening={handleIncidentOpening} />
    </div>
  );
};

const LogsTab = ({ handleIncidentOpening }) => {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const { user, responseErrorHandler, nightMode } = useContext(UserContext);
  const { setup } = useContext(SetupContext);
  const [selectedOri, setSelectedOri] = useState(user?.ori);
  const [oriList, setOriList] = useState([]);
  const [incidentNumber, setIncidentNumber] = useState("");
  const [badge, setBadge] = useState("");
  const [logCode, setLogCode] = useState("");
  const [to, setTo] = useState(moment());
  const [from, setFrom] = useState(moment().subtract(1, "months"));
  const [allOris, setAllOris] = useState(false);

  const [getOris, { loading: orisLoading }] = useLazyQuery(GET_ORIS_QUERY, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "no-cache",
    onError: error => {
      console.error(JSON.stringify(error));
    },
    onCompleted: data => {
      if (data) {
        responseErrorHandler(data);
        setOriList(sortByOriName(data.getOris));
        localStoreData(data, `mp-oris`);
      }
    },
  });

  useEffect(() => {
    if (checkLocalStorageAge(`mp-oris`, 8)) {
      setOriList(sortByOriName(localGetData(`mp-oris`)?.getOris));
    } else {
      !orisLoading && getOris();
    }
  }, [getOris, orisLoading]);

  const [getRadioLog, { data, error, loading }] = useLazyQuery(GET_RADIO_LOG, {
    context: { serializationKey: "incidentQueue" },
    fetchPolicy: "no-cache",
    variables: {
      originatorId: user?.id,
    },
  });

  const handleSearch = (e, searchPage) => {
    e?.preventDefault();
    if (searchPage) {
      setPage(searchPage);
    }
    getRadioLog({
      variables: {
        oriIds: allOris ? null : selectedOri?.id?.toString(),
        incidentNumber: incidentNumber,
        badge: badge,
        logCode: logCode?.name,
        startDate: from?.isValid()
          ? from.utc().format("MM/DD/YYYY HH:mm:ss UTC")
          : null,
        endDate: to?.isValid()
          ? to.utc().format("MM/DD/YYYY HH:mm:ss UTC")
          : null,
        page: searchPage || page,
      },
    });
  };

  useEffect(() => {
    getRadioLog({
      variables: {
        oriIds: allOris ? null : selectedOri?.id?.toString(),
        incidentNumber: incidentNumber,
        badge: badge,
        logCode: logCode?.name,
        startDate: from ? from.utc().format("MM/DD/YYYY HH:mm:ss UTC") : null,
        endDate: to ? to.utc().format("MM/DD/YYYY HH:mm:ss UTC") : null,
        page: page,
      },
    });
    //Only calling once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [cookies] = useCookies();

  return (
    <div style={{ height: "100%" }}>
      <div
        className={
          cookies[`mp-userId-${user?.id}-night-mode`] === "true"
            ? classes.tableH3ContainerDark
            : classes.tableH3ContainerLight
        }
        style={{ borderTopLeftRadius: "0px", borderTopRightRadius: "0px" }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          position="relative"
          width="100%"
        >
          <h3
            data-cy={`h3-table-name-Query-Returns`}
            className={
              cookies[`mp-userId-${user?.id}-night-mode`] === "true"
                ? classes.tableH3TitleDark
                : classes.tableH3TitleLight
            }
          >
            Logs
          </h3>
          &nbsp;&nbsp;
          <HelpIconButton objectname="mobile_logs" />
        </Box>
      </div>
      <form onSubmit={e => handleSearch(e, 1)}>
        <ThemeProvider
          theme={
            cookies[`mp-userId-${user?.id}-night-mode`] === "true"
              ? darkTheme
              : lightTheme
          }
        >
          <Box
            id="log-search-bar"
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            position="relative"
            style={{
              backgroundColor: nightMode ? "#616161" : "#fae8ad",
              padding: "0 10px",
            }}
          >
            <Autocomplete
              id="ori-list-autocomplete"
              options={oriList}
              getOptionLabel={option => option.oriName || ""}
              getOptionSelected={(option, value) =>
                option.id === value.id || ""
              }
              style={{ width: 300 }}
              value={selectedOri}
              renderInput={params => (
                <TextField {...params} label="Select an ORI" />
              )}
              onChange={(e, value) => setSelectedOri(value)}
              autoHighlight
            />
            <TextField
              data-cy="incident-number-search"
              inputProps={{ "data-cy": "incident-number-search-input" }}
              id="incident-number-id"
              label="Incident Number"
              value={incidentNumber}
              className={""}
              onChange={e => setIncidentNumber(e.target.value)}
              margin={"dense"}
            />
            <TextField
              data-cy="badge-search"
              inputProps={{ "data-cy": "badge-search-input" }}
              id="badge-id"
              label="Badge"
              value={badge}
              className={""}
              onChange={e => setBadge(e.target.value)}
              margin={"dense"}
            />
            <Autocomplete
              id="log-codes-autocomplete"
              options={setup?.setupLists?.logCodes}
              getOptionLabel={option => option.name || ""}
              getOptionSelected={(option, value) =>
                option.id === value.id || ""
              }
              style={{ width: 200 }}
              value={logCode}
              renderInput={params => <TextField {...params} label="Log Code" />}
              onChange={(e, value) => setLogCode(value)}
              autoHighlight
            />
            <KeyboardDateTimePicker
              InputLabelProps={{ shrink: true }}
              data-cy="date-time-from"
              clearable={false}
              showTodayButton
              label="From"
              value={from?.local() || null}
              onChange={setFrom}
              format="MM/DD/YYYY HH:mm"
              ampm={false}
            />
            <KeyboardDateTimePicker
              InputLabelProps={{ shrink: true }}
              data-cy="date-time-to"
              clearable={false}
              showTodayButton
              label="To"
              value={to?.local() || null}
              onChange={setTo}
              format="MM/DD/YYYY HH:mm"
              ampm={false}
            />
            <CheckboxWithLabel
              checked={allOris}
              onChange={e => setAllOris(e.target.checked)}
              stateName={"allOris"}
              title={"All Oris"}
              userRoles={user?.roleNames}
              disabled={false}
            />
            <button disabled={loading} onClick={e => handleSearch(e, 1)}>
              {loading ? "Loading" : "Search"}
            </button>
          </Box>
        </ThemeProvider>
      </form>
      {loading && <div>Loading...</div>}
      {error && <div>Error</div>}
      {data && !loading && !error && (
        <TableContainer component={Paper}>
          <Table
            size="small"
            data-cy="logs-tab-table"
            stickyHeader
            aria-label="logs table"
          >
            <TableHead>
              <TableRow>
                <StyledTableHeader>Incident #</StyledTableHeader>
                <StyledTableHeader>Officer</StyledTableHeader>
                <StyledTableHeader>Originator</StyledTableHeader>
                <StyledTableHeader>At</StyledTableHeader>
                <StyledTableHeader>Log Code</StyledTableHeader>
                <StyledTableHeader>Entry</StyledTableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.getRadioLog?.length < 1 || !data?.getRadioLog ? (
                <TableRow>
                  <TableCell>No data</TableCell>
                </TableRow>
              ) : (
                data?.getRadioLog?.map((logEntry) => {
                  return (
                    <TableRow key={logEntry?.id}>
                      <TableCell>
                        <LinkButton
                          handleIncidentOpening={handleIncidentOpening}
                          incident={logEntry?.incident}
                          user={user}
                        />
                      </TableCell>
                      <TableCell>
                        {formatOfficerName(logEntry?.officer)}
                      </TableCell>
                      <TableCell>
                        {formatOfficerName(logEntry?.originator)}
                      </TableCell>
                      <TableCell>
                        {/* {logEntry?.createdAt} */}
                        {logEntry?.createdAt
                          ? moment
                              .utc(
                                logEntry?.createdAt,
                                "YYYY-MM-DD HH:mm:ss UTC",
                              )
                              .local()
                              .format("MM/DD/YYYY HH:mm")
                          : ""}
                      </TableCell>
                      <TableCell>{logEntry?.logCode?.name}</TableCell>
                      <TableCell>
                        <div
                          style={{
                            maxHeight: "15vh",
                            overflow: "auto",
                            maxWidth: "25vw",
                          }}
                        >
                          {logEntry?.logEntry}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
            {
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[25]}
                    colSpan={6}
                    count={-1}
                    rowsPerPage={25}
                    page={page}
                    onPageChange={(e, newPage) => {
                      if (newPage > 0) handleSearch(e, newPage);
                    }}
                    backIconButtonProps={page === 1 ? { disabled: true } : {}}
                    labelDisplayedRows={() =>
                      `${1 + 25 * (page - 1)} - ${25 * page}`
                    }
                  />
                </TableRow>
              </TableFooter>
            }
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default LogsTabWrapper;
